const KR_SDK_CSS = 'https://api.gateway.monetico-retail.com/static/js/krypton-client/V4.0/ext/neon-reset.min.css';
const KR_NEON_JS = 'https://api.gateway.monetico-retail.com/static/js/krypton-client/V4.0/ext/neon.js';

// Promise partagée entre toutes les instances pour ne charger le SDK qu'une seule fois
let sdkLoadPromise = null;

// Flag partagé entre toutes les instances et true si l'utilisateur a soumis le formulaire
let _formSubmitted = false;

// Flag partagé : l'échec de chargement du SDK n'est loggé qu'une seule fois par page
let _sdkLoadErrorLogged = false;

// Délai max de chargement du SDK avant d'afficher un message (ex : script bloqué sans erreur réseau)
const SDK_LOAD_TIMEOUT = 20000;

// Erreurs KR pour lesquelles le SDK affiche déjà un message explicite (carte refusée, champ invalide) :
// inutile d'y ajouter le conseil « bloqueur de publicités »
const KR_FUNCTIONAL_ERROR = /^(ACQ_|CLIENT_3)/;

export default class MoneticoRetail extends HTMLDivElement {

    connectedCallback() {
        this.formToken = this.dataset.formToken;
        this.successUrl = this.dataset.successUrl;
        this.is3x = this.dataset.is3x === '1';
        this.inline = this.dataset.inline === '1';
        this.inlineId = this.dataset.inlineId;
        this.logPayButtonClickUrl = this.dataset.logPayButtonClickUrl;
        this.logQuitUrl = this.dataset.logQuitUrl;
        this.logBackNavigationUrl = this.dataset.logBackNavigationUrl;
        this.logPayButtonErrorUrl = this.dataset.logPayButtonErrorUrl;
        this.logJsErrorUrl = this.dataset.logJsErrorUrl;
        this.cartId = this.dataset.cartId;

        if (!window._moneticoQuitListenerRegistered) {
            window._moneticoQuitListenerRegistered = true;
            window.addEventListener('pagehide', () => {
                if (!_formSubmitted) {
                    sessionStorage.setItem('monetico_user_quit_' + this.cartId, '1');
                    this.logQuitWithoutPaying();
                }
            });
            window.addEventListener('pageshow', () => {
                const key = 'monetico_user_quit_' + this.cartId;
                if (sessionStorage.getItem(key)) {
                    sessionStorage.removeItem(key);
                    this.logBackNavigation();
                }
            });
        }

        const sidebarBtn = this.querySelector('#monetico-retail-pay-btn');

        if (!sidebarBtn || sidebarBtn.dataset.paymentDisabled) {
            return;
        }

        if (!this.formToken) {
            this.showError();
            return;
        }

        // Masquer le composant sidebar (moteur invisible pour le SDK)
        this.style.display = 'none';

        if (this.inline) {
            this.btn = sidebarBtn;
        } else if (this.isCurrentSelection()) {
            this.injectPopin();
        }

        // Si le SDK est déjà chargé, initialiser directement. Sinon, le charger.
        if (window._krLoaded && typeof KR !== 'undefined') {
            requestAnimationFrame(() => this.bindKR());
        } else {
            this.loadSDK()
                .then(() => requestAnimationFrame(() => this.bindKR()))
                .catch((reason) => this.onSDKLoadError(reason));
        }
    }

    loadSDK() {
        // Réutiliser la promise si le SDK est déjà en cours de chargement par une autre instance
        if (sdkLoadPromise) {
            return sdkLoadPromise;
        }

        sdkLoadPromise = new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('timeout')), SDK_LOAD_TIMEOUT);
            const done = () => {
                clearTimeout(timeout);
                resolve();
            };

            const cssLink = document.createElement('link');
            cssLink.rel = 'stylesheet';
            cssLink.href = KR_SDK_CSS;
            cssLink.crossOrigin = 'anonymous';
            document.head.appendChild(cssLink);

            const neonPreload = document.createElement('link');
            neonPreload.rel = 'preload';
            neonPreload.href = KR_NEON_JS;
            neonPreload.as = 'script';
            document.head.appendChild(neonPreload);

            const script = document.createElement('script');
            script.src = this.dataset.jsLibrary;
            script.crossOrigin = 'anonymous';
            script.setAttribute('kr-public-key', this.dataset.publicKey);
            script.setAttribute('kr-language', 'fr-FR');
            script.addEventListener('load', () => {
                window._krLoaded = true;

                const neon = document.createElement('script');
                neon.src = KR_NEON_JS;
                neon.addEventListener('load', done);
                // neon.js n'est que le thème : s'il est bloqué, le formulaire reste utilisable
                neon.addEventListener('error', done);
                document.head.appendChild(neon);
            });
            // Script bloqué (bloqueur de publicités, pare-feu, réseau...) : sans ce handler, loader infini
            script.addEventListener('error', () => {
                clearTimeout(timeout);
                reject(new Error('script_error'));
            });
            document.head.appendChild(script);
        });

        return sdkLoadPromise;
    }

    onSDKLoadError(reason) {
        if (!_sdkLoadErrorLogged) {
            _sdkLoadErrorLogged = true;
            this.logJsError('sdk_load', reason?.message || 'unknown', this.dataset.jsLibrary);
        }

        this.showError(this.dataset.sdkBlockedMessage);
    }

    onKRError(event) {
        const code = event?.errorCode || '';
        this.logJsError('kr_error', code, event?.detailedErrorMessage || event?.errorMessage || '');

        if (KR_FUNCTIONAL_ERROR.test(code) || !this.isCurrentSelection()) {
            return;
        }

        this.showHint(this.dataset.adblockHintMessage);
    }

    createInlineForm() {
        const old = document.getElementById('kr-smart-form-init');
        if (old) old.remove();

        const inlineContainer = document.getElementById('monetico-inline-container-' + this.inlineId);
        if (!inlineContainer) {
            return;
        }

        const smartFormWrapper = document.createElement('div');
        smartFormWrapper.id = 'kr-smart-form-init';
        smartFormWrapper.style.display = 'none';
        smartFormWrapper.innerHTML = '<div class="kr-smart-form" kr-card-form-expanded kr-grid kr-opm-grid-justify="stretch"></div>';
        inlineContainer.appendChild(smartFormWrapper);
    }

    injectPopin() {
        const embedded = document.createElement('div');
        embedded.className = 'kr-smart-form';
        embedded.setAttribute('kr-popin', '');
        embedded.style.display = 'none';
        this.appendChild(embedded);

        const style = document.createElement('style');
        style.textContent = '.kr-popin-button { display: none !important; }';
        this.appendChild(style);
    }

    bindInstallmentPreselection(container) {
        setTimeout(() => {
            const options = container.querySelectorAll('.kr-sp-panel-option');

            for (let i = 0; i < options.length; i++) {
                const label = options[i].querySelector('.kr-sp-option-activator-label');
                if (label && /échéance/i.test(label.textContent) && !options[i].classList.contains('kr-sp-panel-option--selected')) {
                    const activator = options[i].querySelector('.kr-sp-option-activator');
                    if (activator) activator.click();
                    break;
                }
            }
        }, 150);
    }

    /**
     * Ce moteur correspond-il au moyen RÉELLEMENT sélectionné (radio coché) et est-il toujours dans le DOM ?
     */
    isCurrentSelection() {
        if (!this.isConnected) {
            return false;
        }
        const radio = document.getElementById('payment-method-' + this.inlineId);
        return !!radio && radio.checked;
    }

    bindKR() {
        if (!this.isCurrentSelection()) {
            return;
        }

        this._renderStart = Date.now();

        // Filet de cohérence : retirer un éventuel formulaire inline résiduel avant de rendre.
        const staleInlineForm = document.getElementById('kr-smart-form-init');
        if (staleInlineForm) staleInlineForm.remove();

        KR.removeEventCallbacks();

        KR.onError((event) => this.onKRError(event));

        KR.onSubmit(() => {
            _formSubmitted = true;
            if (this.inline) this.logPayButtonClick();
            window.location.href = this.successUrl;
            return false;
        });

        KR.onError(e => {
            console.warn('Monetico KR error', e?.errorCode, e?.errorMessage, e?.paymentMethod);

            // CLIENT_101 = transaction abandonnée par l'acheteur. C'est le seul signal émis quand le moyen s'ouvre
            // dans une popup (virement instantané) : ni onPopinClosed ni le rejet de openPaymentMethod ne surviennent.
            if (!this.inline && e?.errorCode === 'CLIENT_101') {
                this.refreshFormToken();
            }
        });

        if (this.inline) {
            KR.onFormReady(() => this.onInlineFormReady());
        } else {
            KR.onPopinClosed(() => this.refreshFormToken());
        }

        KR.removeForms()
            .then(() => {
                if (!this.isCurrentSelection()) {
                    return;
                }
                return KR.setFormConfig({ formToken: this.formToken });
            })
            .then(() => {
                if (!this.isCurrentSelection()) {
                    return;
                }

                if (this.inline) {
                    // Séquence officielle du SDK : DOM ajouté APRÈS l'init, puis renderElements().
                    // Le formulaire est révélé et le loader masqué par onInlineFormReady.
                    this.createInlineForm();
                    return KR.renderElements('#kr-smart-form-init');
                }

                // Popin : le bouton (déjà présent dans l'accordéon) ouvre la popin à la demande.
                this.logRenderTime();
                this.btn = document.getElementById('monetico-popin-btn-' + this.inlineId);
                if (this.btn) this.enableButton(() => KR.openPaymentMethod());
            })
            .catch(e => {
                console.error('Monetico KR error', e);
                if (this.isCurrentSelection()) this.showError();
            });
    }

    onInlineFormReady() {
        if (!this.isCurrentSelection()) {
            return;
        }

        this.logRenderTime();

        const loader = document.getElementById('payment-means-loader-' + this.inlineId);
        const initWrapper = document.getElementById('kr-smart-form-init');

        if (initWrapper) initWrapper.style.display = '';
        if (loader) loader.style.display = 'none';

        if (this.is3x) {
            // Par défaut l'ouverture du formulaire 3x sans frais de monetico retail s'ouvre sur le paiement comptant
            // avec une option 3x, du coup on force le click sur l'option 3x pour éviter toute confusion
            const inlineContainer = document.getElementById('monetico-inline-container-' + this.inlineId);
            if (inlineContainer) this.bindInstallmentPreselection(inlineContainer);
        }
    }

    postLog(url, body) {
        if (!url) {
            return;
        }

        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf || '' },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => {});
    }

    logJsError(context, code, message) {
        this.postLog(this.logJsErrorUrl, {
            cartId: this.cartId,
            context,
            code,
            message,
        });
    }

    logPayButtonClick() {
        this.postLog(this.logPayButtonClickUrl, { cartId: this.cartId });
    }

    logQuitWithoutPaying() {
        this.postLog(this.logQuitUrl, { cartId: this.cartId });
    }

    logBackNavigation() {
        this.postLog(this.logBackNavigationUrl, { cartId: this.cartId });
    }

    logPayButtonError(error) {
        this.postLog(this.logPayButtonErrorUrl, {
            cartId: this.cartId,
            error: error ? String(error.message || error) : null,
        });
    }

    logRenderTime() {
        if (!this._renderStart || !this.dataset.logRenderTimeUrl) {
            return;
        }

        const time = Date.now() - this._renderStart;
        this._renderStart = null;

        this.postLog(this.dataset.logRenderTimeUrl, {
            time,
            name: this.dataset.paymentMeansName,
            cartId: this.cartId,
        });
    }

    /**
     * Certains moyens sont marqués `retrySupport: PREVENTS_RETRY` par la passerelle (virement instantané) : le SDK
     * mémorise la tentative dans son store et refuse toute réouverture avec un CLIENT_107, même après un abandon.
     * Ce verrou est purement local et n'est levé que par un nouveau formToken. On rejoue donc le cycle de sélection
     * du moyen (nouveau CreatePayment côté serveur puis re-render du composant), ce que faisait déjà à la main un
     * simple rechargement de page.
     */
    refreshFormToken() {
        if (_formSubmitted || this._refreshing) {
            return;
        }

        const radio = document.getElementById('payment-method-' + this.inlineId);
        if (!radio || !radio.checked) {
            return;
        }

        this._refreshing = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    enableButton(onClick) {
        if (!this.inline) {
            const loader = document.getElementById('payment-means-loader-' + this.inlineId);
            if (loader) loader.style.display = 'none';
            this.btn.style.display = '';
        }

        this.btn.removeAttribute('disabled');
        this.btn.onclick = () => {
            this.logPayButtonClick();
            Promise.resolve()
                .then(() => onClick())
                .catch(e => {
                    console.error('Monetico KR openPaymentMethod error', e);
                    this.logPayButtonError(e);
                    this.showError();
                });
        };
    }

    /**
     * Affiche un conseil sous le formulaire, sans le remplacer (le SDK y affiche déjà son propre message).
     */
    showHint(message) {
        const container = document.getElementById('monetico-inline-container-' + this.inlineId)
            || document.querySelector('#multiCollapse-' + this.inlineId + ' .card-radio');

        if (!container || !message || container.querySelector('.monetico-adblock-hint')) {
            return;
        }

        const alert = document.createElement('div');
        alert.className = 'alert alert-warning mt-3 mb-0 monetico-adblock-hint';
        alert.textContent = message;
        container.appendChild(alert);
    }

    showError(message = null) {
        const loader = document.getElementById('payment-means-loader-' + this.inlineId);
        const container = loader ? loader.parentElement : document.querySelector('#multiCollapse-' + this.inlineId + ' .card-radio');

        if (container) {
            const alert = document.createElement('div');
            alert.className = 'alert alert-warning mb-0';
            alert.textContent = message || this.dataset.errorMessage;
            container.replaceChildren(alert);
        }
    }
}
